import React from "react";
import { View } from "@instructure/ui-view";
import { Tabs } from "@instructure/ui-tabs";
import { Button } from "@instructure/ui-buttons";
import { SimpleSelect } from "@instructure/ui-simple-select";
import { TextInput } from "@instructure/ui-text-input";
import { IconDownloadLine } from "@instructure/ui-icons";
import ShowDTI from "./components/dti/ShowDTI";
import SortableTable from "./components/stats/SortableTable";
import StatsSearch from "./components/stats/StatsSearch";
import { getWeekRange, timeToHuman } from "./components/stats/utils";
import "./components/stats/stats.css";

const TEACHER_HEADERS = [
  { id: "GiaoVienID", text: "GiaoVienID" },
  { id: "name", text: "Họ và tên", width: "40%" },
  { id: "num_of_modules", text: "Tổng module đã công bố" },
  { id: "num_of_items", text: "Tổng nội dung đã công bố" },
];

function App() {
  const [date] = React.useState(new Date());
  const [week, setWeek] = React.useState(new Date().getWeek() - 1);
  const [data, setData] = React.useState(null);
  const [dataByWeek, setDataByWeek] = React.useState(null);
  const [subjects, setSubjects] = React.useState(null);
  const [valueSubject, setValueSubject] = React.useState("");
  const [lmsDomain, setLmsDomain] = React.useState(null);
  const [searchData, setSearchData] = React.useState(null);
  const [searchDataByWeek, setSearchDataByWeek] = React.useState(null);
  const [searchBy, setSearchBy] = React.useState("teacher");
  const [placeholder, setPlaceholder] = React.useState("Tìm kiếm theo tên giáo viên");
  const [selectedIndex, setSelectedIndex] = React.useState(0);

  const bootstrapTokenFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    if (!token) return;
    localStorage.setItem("token", token);
    params.delete("token");
    const search = params.toString();
    const nextUrl = `${window.location.pathname}${search ? `?${search}` : ""}`;
    window.history.replaceState({}, document.title, nextUrl);
  };

  const handleTabChange = (_event, { index }) => {
    setSelectedIndex(index);
  };

  React.useEffect(() => {
    bootstrapTokenFromUrl();
  }, []);

  React.useEffect(() => {
    fetch("/api/v1/session")
      .then((res) => res.json())
      .then((session) => {
        setLmsDomain(session.custom_domain);
        fetch(
          `/api/v1/teachers?custom_root_account_uuid=${encodeURIComponent(session.custom_root_account_uuid)}&custom_canvas_account_id=${encodeURIComponent(session.custom_canvas_account_id)}`,
        )
          .then((res) => res.json())
          .then((payload) => {
            setData(payload.data);
            setSubjects(payload.subjects);
          });

        fetch(
          `/api/v1/teachers_byweek?year=${new Date().getFullYear()}&week_number=${week}&custom_root_account_uuid=${encodeURIComponent(session.custom_root_account_uuid)}&custom_canvas_account_id=${encodeURIComponent(session.custom_canvas_account_id)}`,
        )
          .then((res) => res.json())
          .then((payload) => {
            setDataByWeek(payload.data);
            setSubjects(payload.subjects);
          });
      });
  }, []);

  React.useEffect(() => {
    fetch("/api/v1/session")
      .then((res) => res.json())
      .then((session) => {
        setLmsDomain(session.custom_domain);
        fetch(
          `/api/v1/teachers_byweek?year=${new Date().getFullYear()}&week_number=${week}&custom_root_account_uuid=${encodeURIComponent(session.custom_root_account_uuid)}&custom_canvas_account_id=${encodeURIComponent(session.custom_canvas_account_id)}`,
        )
          .then((res) => res.json())
          .then((payload) => {
            setDataByWeek(payload.data);
            setSubjects(payload.subjects);
          });
      });
  }, [week]);

  const searchFilter = (by, value, sourceData, sourceDataByWeek) => {
    const normalized = value.toLowerCase();
    if (by === "teacher") {
      const filtered = sourceData.filter(
        (row) =>
          row.GiaoVienID.toLowerCase().includes(normalized) ||
          row.name.toLowerCase().includes(normalized),
      );
      setSearchData(filtered);
      const filteredWeek = sourceDataByWeek.filter(
        (row) =>
          row.GiaoVienID.toLowerCase().includes(normalized) ||
          row.name.toLowerCase().includes(normalized),
      );
      setSearchDataByWeek(filteredWeek);
      return;
    }

    const filtered = sourceData.filter((row) =>
      row.courses.find((course) => course.TenMon.toLowerCase().includes(normalized)),
    );
    setSearchData(filtered);
    const filteredWeek = sourceDataByWeek.filter((row) =>
      row.courses.find((course) => course.TenMon.toLowerCase().includes(normalized)),
    );
    setSearchDataByWeek(filteredWeek);
  };

  const search = (_event, value) => {
    if (!data || !dataByWeek) return;
    searchFilter(searchBy, value, data, dataByWeek);
  };

  const searchByWeek = (_event, value) => {
    if (!data || !dataByWeek) return;
    searchFilter(searchBy, value, data, dataByWeek);
  };

  const handleSearchBy = (_event, payload) => {
    if (payload.value === "teacher") {
      setPlaceholder("Tìm kiếm theo tên giáo viên");
      if (data && dataByWeek) searchFilter("teacher", "", data, dataByWeek);
    } else {
      setPlaceholder("Chọn môn học");
      if (data && dataByWeek) {
        const subject = valueSubject || subjects?.[0] || "";
        searchFilter("subject", subject, data, dataByWeek);
      }
    }
    setSearchBy(payload.value);
  };

  const handleSubjectSelect = (_event, { value }) => {
    setValueSubject(value);
    if (data && dataByWeek) searchFilter("subject", value, data, dataByWeek);
  };

  const changeWeek = (_event, { value }) => {
    setWeek(value);
  };

  const renderFilterInput = (onChange) => {
    if (searchBy === "teacher") {
      return <TextInput placeholder={placeholder} onChange={onChange} />;
    }
    return (
      <SimpleSelect value={valueSubject} onChange={handleSubjectSelect}>
        {(subjects || []).map((opt, index) => (
          <SimpleSelect.Option key={opt} id={`opt-${index}`} value={opt}>
            {opt}
          </SimpleSelect.Option>
        ))}
      </SimpleSelect>
    );
  };

  const renderTeacherTable = (rows, emptyMessage) => {
    if (!rows) {
      return <div className="stats-empty">Đang tải dữ liệu...</div>;
    }
    if (rows.length <= 0) {
      return <div className="stats-empty">{emptyMessage}</div>;
    }
    return (
      <SortableTable
        headers={TEACHER_HEADERS}
        rows={rows}
        perPage={10}
        lms_domain={lmsDomain}
      />
    );
  };

  const lastUpdated = (rows) => {
    if (!rows) return "Đang tải...";
    if (rows.length <= 0) return "Dữ liệu trống";
    return timeToHuman(rows[0].updated_at);
  };

  return (
    <View as="div" margin="none" padding="none">
      <Tabs margin="none" padding="none" onRequestTabChange={handleTabChange}>
        <Tabs.Panel
          id="tkhd"
          renderTitle="Thống kê hoạt động"
          isSelected={selectedIndex === 0}
        >
          <div className="stats-page">
            <header className="stats-hero">
              <span className="stats-hero__badge">Thống kê LMS</span>
              <h1 className="stats-hero__title">Hoạt động giáo viên trên hệ thống</h1>
              <p className="stats-hero__desc">
                Tổng hợp module và nội dung đã công bố theo từng giáo viên. Nhấn tên giáo viên để xem chi tiết khóa học.
              </p>
            </header>

            <div className="stats-panel">
              <div className="stats-toolbar">
                <p className="stats-toolbar__meta">
                  Cập nhật lần cuối: <strong>{lastUpdated(data)}</strong>
                </p>
                <Button
                  as="a"
                  href="/api/v1/teachers/exports"
                  target="_blank"
                  color="primary"
                  renderIcon={IconDownloadLine}
                >
                  Xuất báo cáo
                </Button>
              </div>

              <StatsSearch
                searchBy={searchBy}
                onSearchByChange={handleSearchBy}
                placeholder={placeholder}
                renderFilterInput={() => renderFilterInput(search)}
              />

              {renderTeacherTable(
                searchData || data,
                "Không có dữ liệu",
              )}
            </div>
          </div>
        </Tabs.Panel>

        <Tabs.Panel
          id="week-tab"
          renderTitle="Thống kê theo tuần"
          isSelected={selectedIndex === 1}
        >
          <div className="stats-page">
            <header className="stats-hero">
              <span className="stats-hero__badge">Thống kê theo tuần</span>
              <h1 className="stats-hero__title">Hoạt động giáo viên theo tuần</h1>
              <p className="stats-hero__desc">
                Theo dõi module và nội dung giáo viên công bố trong từng tuần học.
              </p>
            </header>

            <div className="stats-panel">
              <div className="stats-toolbar">
                <p className="stats-toolbar__meta">
                  Cập nhật lần cuối: <strong>{lastUpdated(dataByWeek)}</strong>
                </p>
                <Button
                  as="a"
                  href={`/api/v1/teachers/exports_byweek?week_number=${week}&year=${new Date().getFullYear()}`}
                  target="_blank"
                  color="primary"
                  renderIcon={IconDownloadLine}
                >
                  Xuất báo cáo
                </Button>
              </div>

              <div className="stats-week-select">
                <SimpleSelect onChange={changeWeek} defaultValue={week}>
                  {Array.from(Array(date.getWeek()).keys()).map((value) => {
                    if (value > 0) {
                      const range = getWeekRange(value);
                      return (
                        <SimpleSelect.Option key={value} id={String(value)} value={value}>
                          {`Tuần ${value} (Từ ${range.start_date} đến ${range.end_date})`}
                        </SimpleSelect.Option>
                      );
                    }
                    return null;
                  })}
                </SimpleSelect>
              </div>

              <StatsSearch
                searchBy={searchBy}
                onSearchByChange={handleSearchBy}
                placeholder={placeholder}
                renderFilterInput={() => renderFilterInput(searchByWeek)}
              />

              {renderTeacherTable(
                searchDataByWeek || dataByWeek,
                "Không có dữ liệu theo tuần",
              )}
            </div>
          </div>
        </Tabs.Panel>

        <Tabs.Panel
          id="dti-tab"
          renderTitle="Báo cáo DTI"
          isSelected={selectedIndex === 2}
        >
          <ShowDTI />
        </Tabs.Panel>
      </Tabs>
    </View>
  );
}

export default App;
